import React, { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Button, Image, Form } from 'react-bootstrap';
import '../../styles/login.css';
import { useThemeMode } from '../../ThemeContext';
import { dataAPI } from '../../helpers/api_service';
import { AlertDialog } from '../Common';

// Import assets
import LoginBanner1 from '../../assets/loginban1.png';
import ChevronRight from '../../assets/chevron-right.svg';
import CompanyIcon from '../../assets/iccompany.svg';
import IndividualIcon from '../../assets/icindividual.svg';

function CompleteProfile() {
  const { mode } = useThemeMode();
  const navigate = useNavigate();
  
  // State for form data
  const [formData, setFormData] = useState({
    accountType: '',
    industry_type_id: '',
    region_id: ''
  });
  
  // State for dropdown data
  const [regions, setRegions] = useState([]);
  const [industryTypes, setIndustryTypes] = useState([]);
  const [isLoadingData, setIsLoadingData] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertConfig, setAlertConfig] = useState({
    type: 'warning',
    title: '',
    message: ''
  });
  


  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle dropdown changes
  const handleDropdownChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle account type selection
  const handleAccountTypeSelect = (type) => {
    setFormData(prev => ({
      ...prev,
      accountType: type
    }));
  };



  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    if (!formData.accountType) {
      setAlertConfig({
        type: 'warning',
        title: 'Account Type Required',
        message: 'Please select an account type to continue.'
      });
      setShowAlert(true);
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Call the complete profile API
      const response = await dataAPI.completeProfile({
        company_type: formData.accountType,
        region_id: parseInt(formData.region_id),
        industry_type_id: parseInt(formData.industry_type_id)
      });

      if (response.status === 'success') {
        console.log('Profile completion successful:', response);
        
        // Check if new_token is provided in the response data and update localStorage
        if (response.data && response.data.new_token) {
          localStorage.setItem('authToken', response.data.new_token);
          console.log('Updated auth token with new token from profile completion');
        }
        
        navigate('/dashboard');
      } else {
        setAlertConfig({
          type: 'error',
          title: 'Profile Completion Failed',
          message: response.message || 'Failed to complete profile. Please try again.'
        });
        setShowAlert(true);
      }
      
    } catch (error) {
      console.error('Error completing profile:', error);
      setAlertConfig({
        type: 'error',
        title: 'Profile Completion Failed',
        message: 'Failed to complete profile. Please try again.'
      });
      setShowAlert(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Fetch dropdown data on component mount
  useEffect(() => {
    const fetchDropdownData = async () => {
      try {
        const data = await dataAPI.getRegionIndustryList();
        
        if (data.status === 'success') {
          setRegions(data.data.regions || []);
          setIndustryTypes(data.data.industry_types || []);
        } else {
          console.error('Failed to fetch dropdown data:', data.message);
        }
      } catch (error) {
        console.error('Error fetching dropdown data:', error);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchDropdownData();
  }, []);

  return (
    <div className={`login-body ${mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
      <Container fluid className="login-container">
        <Row className="g-0 min-vh-100">
          {/* Left Side: Image/Overlay */}
          <Col lg={6} className="d-none d-lg-block login-left-bg position-relative">
            <div className="login-slider-img-wrap position-absolute w-100 h-100">
              <Image src={LoginBanner1} alt="Complete Profile Banner" className="login-slider-img" fluid />
            </div>
            <div className="login-left-overlay"></div>
          </Col>
          
          {/* Right Side: Complete Profile Form */}
          <Col xs={12} lg={6} className="d-flex align-items-center justify-content-center login-right-bg position-relative">
            <div className="login-blur-bg-top"></div>
            <div className="login-blur-bg-bottom"></div>
            <div className="login-form-wrapper w-100" style={{ maxWidth: '486px' }}>
              <div className="mb-4 text-center" style={{ marginTop: 32 }}>
                <h2 className="login-title" style={{ color: mode === 'dark' ? '#26c3c0' : '#009391' }}>
                  Complete Your Profile
                </h2>
                <div className="login-subtitle" style={{ color: mode === 'dark' ? '#8a8a8a' : '#666' }}>
                  Let's get to know you better to personalize your experience.
                </div>
              </div>

              <Form onSubmit={handleSubmit}>
                {/* Account Type Selection */}
                <div className="mb-4">
                  <Row className="g-3">
                    <Col xs={12}>
                      <div
                        className={`account-type-card selectable-card${formData.accountType === 'company' ? ' selected' : ''}`}
                        tabIndex={0}
                        onClick={() => handleAccountTypeSelect('company')}
                        style={{ cursor: 'pointer' }}>
                        <div className="d-flex align-items-center gap-3">
                          <span className="account-type-icon">
                            <Image src={CompanyIcon} alt="Company" width={40} height={40} />
                          </span>
                          <div>
                            <div className="fw-bold text-white">Company Account</div>
                            <div className="text-secondary small">For businesses and organizations</div>
                          </div>
                        </div>
                      </div>
                    </Col>
                    <Col xs={12}>
                      <div
                        className={`account-type-card selectable-card${formData.accountType === 'individual' ? ' selected' : ''}`}
                        tabIndex={0}
                        onClick={() => handleAccountTypeSelect('individual')}
                        style={{ cursor: 'pointer' }}
                      >
                        <div className="d-flex align-items-center gap-3">
                          <span className="account-type-icon">
                            <Image src={IndividualIcon} alt="Individual" width={40} height={40} />
                          </span>
                          <div>
                            <div className="fw-bold text-white">Individual Account</div>
                            <div className="text-secondary small">For personal and freelance use</div>
                          </div>
                        </div>
                      </div>
                    </Col>
                  </Row>
                </div>

                {/* Industry Type Dropdown */}
                <div className="mb-4">
                  <Form.Select
                    name="industry_type_id"
                    value={formData.industry_type_id}
                    onChange={handleDropdownChange}
                    className="login-input"
                    disabled={isLoadingData}
                    required
                  >
                    <option value="">Select Industry Type</option>
                    {industryTypes.map((industry) => (
                      <option key={industry.id} value={industry.id}>
                        {industry.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                {/* Region Dropdown */}
                <div className="mb-4">
                  <Form.Select
                    name="region_id"
                    value={formData.region_id}
                    onChange={handleDropdownChange}
                    className="login-input"
                    disabled={isLoadingData}
                    required
                  >
                    <option value="">Select Region</option>
                    {regions.map((region) => (
                      <option key={region.id} value={region.id}>
                        {region.name}
                      </option>
                    ))}
                  </Form.Select>
                </div>

                {/* Submit Button */}
                <Button
                  type="submit"
                  className="btn btn-teal w-100 login-btn d-flex align-items-center justify-content-center mb-3"
                  disabled={isSubmitting || isLoadingData}
                >
                  {isSubmitting ? 'Completing Profile...' : 'Complete Profile'}
                  <span className="ms-2">
                    <Image src={ChevronRight} alt="chevron" style={{ height: '14px' }} />
                  </span>
                </Button>

                {/* Skip for now link */}
                <div className="text-center mb-4">
                  <Link to="/dashboard" className="login-signup-link">
                    Skip for now
                  </Link>
                </div>
              </Form>
            </div>
          </Col>
        </Row>
      </Container>
      
      {/* Alert Dialog */}
      <AlertDialog
        isOpen={showAlert}
        onClose={() => setShowAlert(false)}
        type={alertConfig.type}
        title={alertConfig.title}
        message={alertConfig.message}
        confirmText="OK"
      />
    </div>
  );
}

export default CompleteProfile; 